import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { JobDTO } from '@/types';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';

type Proceed = () => void | Promise<void>;

/**
 * Guards balance-affecting changes on a *settled* job (req 11). When the job's balance has been
 * settled, wrapping an action in `guard(proceed)` first prompts the user with three choices:
 *   - Keep settled  → run the change, leave the settle timestamp in place.
 *   - Unsettle      → clear the settle timestamp, then run the change.
 *   - Cancel        → abort the change.
 * When the job is not settled, `proceed` runs immediately with no prompt.
 *
 * Render `guardDialog` somewhere in the component tree for the prompt to appear.
 */
export function useSettledJobGuard(job: JobDTO | null | undefined, onJobUpdated?: (j: JobDTO) => void) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Proceed | null>(null);

  const applyJob = useCallback((updated: JobDTO) => {
    queryClient.setQueryData(['job', updated.id], updated);
    queryClient.setQueriesData({ queryKey: ['jobs'] }, (existing: any) => {
      if (!existing) return existing;
      const isArray = Array.isArray(existing);
      const items = isArray ? existing : existing.jobs;
      if (!Array.isArray(items)) return existing;
      const updatedItems = items.map((j: JobDTO) => (j.id === updated.id ? updated : j));
      return isArray ? updatedItems : { ...existing, jobs: updatedItems };
    });
    onJobUpdated?.(updated);
  }, [queryClient, onJobUpdated]);

  const guard = useCallback((proceed: Proceed) => {
    if (job?.balance_settled_at != null) {
      // Store the callback (functional-update form so React doesn't invoke it as an updater).
      setPending(() => proceed);
    } else {
      void proceed();
    }
  }, [job]);

  const keepSettled = async () => {
    const p = pending;
    if (p) await p();
  };

  const unsettleThenProceed = async () => {
    if (job) {
      const updated = await apiService.unsettleJobBalance(job.id);
      applyJob(updated);
    }
    const p = pending;
    if (p) await p();
  };

  const guardDialog = pending ? (
    <ConfirmDialog
      isOpen
      onClose={() => setPending(null)}
      title="This job's balance is settled"
      message={destructiveCopy.settledJobChange}
      confirmText="Keep settled"
      confirmButtonClassName="bg-blue-600 hover:bg-blue-700 text-white"
      secondaryText="Unsettle"
      onSecondary={unsettleThenProceed}
      onConfirm={keepSettled}
      cancelText="Cancel change"
    />
  ) : null;

  return { guard, guardDialog };
}
